// Practice focus mode — shared cross-component flag and action-bar state.
//
// While a practice is mid-take, the practice-group layout swaps the global
// `PracticeNav` for a slim sticky action bar (session name / timer / a
// pause/resume mirror / End / Exit). Practice pages flip the focus flag at
// Start / End / Exit; the layout reads `$focusStore.active` and
// `PracticeFocusShell` renders one of two layouts.
//
// The action-bar contents derive entirely from a single `recordingState`
// field — `'idle' | 'recording' | 'paused' | 'ended'`
// (wiki/feature-session-ending.md §5). This replaced the bare `paused`
// boolean: the start CTA only ENTERS focus + generates content (recordingState
// 'idle', timer frozen); the single 3-state recording control drives
// idle → recording → paused; End / timer-up flips to 'ended' (focus mode
// persists, post-take content shows); Exit tears focus down.
//
// See wiki/feature-practice-session.md + wiki/feature-session-ending.md for the
// user-facing spec.
//
// Why a writable store rather than a $state rune?
//   The flag is read from at least three places (the layout, the shell, and
//   individual practice pages) and SvelteKit `+layout.svelte` is a Svelte 4
//   component today — a `writable` is the lowest-friction shared seam that
//   both Svelte 4 hosts (the layout) and Svelte 5 runes hosts (the practice
//   pages we are not migrating) can consume identically via `$focusStore`.

import { writable } from 'svelte/store';

/**
 * The recording-state machine driving the focus action bar
 * (wiki/feature-session-ending.md §5):
 *   - `idle`      — focus entered, content generated, but the take has NOT
 *                   begun. Timer frozen at `0:00 / m:ss`. Action bar = Exit
 *                   only; content CTA = "Start recording".
 *   - `recording` — the take is live: recorder running, timer ticking. Action
 *                   bar = "Pause recording" mirror + End; content CTA =
 *                   "Pause recording".
 *   - `paused`    — the take is paused (recorder.pause(), timer frozen). Action
 *                   bar = "Resume recording" mirror + End; content CTA =
 *                   "Resume recording".
 *   - `ended`     — the take has ended (recorder finalised, timer stopped).
 *                   Focus mode PERSISTS, showing the post-take/playback
 *                   content. Action bar = Exit only.
 */
export type RecordingState = 'idle' | 'recording' | 'paused' | 'ended';

/**
 * Why a `paused` take is paused: the speaker pressed Pause, or the tab was
 * hidden mid-take. Null whenever the take is not paused.
 */
export type PauseReason = 'manual' | 'hidden' | null;

/**
 * What entering this focus session actually consumed — drives the Exit-confirm
 * dialog's forfeit wording (wiki/feature-session-ending.md §4):
 *   - `credit` — an AI credit was spent (Active Listening's speaker generation);
 *   - `quota`  — a daily practice session was spent (a successful online
 *                content generation);
 *   - `none`   — nothing was charged (offline in-browser generation, a manual
 *                Rehearsal take, or a generation that failed). Exit needs no
 *                confirmation.
 */
export type PracticeSpend = 'credit' | 'quota' | 'none';

export interface FocusState {
  /** True while a take is in flight — the layout hides `PracticeNav`. */
  active: boolean;
  /**
   * Display name of the running session (e.g. "Bemused Quokka"). Title-cased
   * for presentation by the consumer; the store accepts whatever the practice
   * page hands in. Kept as the BARE playful name (download filenames slugify
   * it); the shell composes the focus-bar title from it + `practiceLabel`.
   */
  sessionName: string | null;
  /**
   * Localized display label of the running practice (e.g. "Active Listening"),
   * or null when unknown. The shell composes the focus-bar title
   * "<sessionName> · <practiceLabel>" from the two fields.
   */
  practiceLabel: string | null;
  /**
   * What this focus session consumed so far (see {@link PracticeSpend}). Reset
   * to `'none'` on every focus entry; the runner flips it when the charge
   * actually lands (generation success / page-side credit spend).
   */
  exitSpend: PracticeSpend;
  /**
   * Pre-formatted timer string ("1:23" or "1:23 / 5:00"). The shell does not
   * format — pages push the exact display string on each tick.
   */
  timer: string | null;
  /**
   * The recording-state machine (wiki/feature-session-ending.md §5). Replaces
   * the bare `paused` boolean. The shell derives every action-bar control from
   * this: the pause/resume mirror appears only in `recording`/`paused`, End
   * only in `recording`/`paused`, Exit only in `idle`/`ended`.
   */
  recordingState: RecordingState;
  /**
   * Why the take is paused. A tab-hidden pause keeps the music bed playing as a
   * reminder that a take is waiting; a manual pause silences it.
   */
  pauseReason: PauseReason;
  /**
   * Remaining seconds to the planned length (`plannedSeconds - elapsedSeconds`,
   * clamped ≥ 0), or null when no planned length is known yet. The shell drives
   * the ending-soon countdown emphasis from this
   * (wiki/feature-session-ending.md §2.1). Frozen while `recordingState` is
   * `idle` (timer not started) or `paused`.
   */
  remainingSeconds: number | null;
  /**
   * The take's planned length in seconds, pushed once when the timer starts and
   * held for the take. Beside {@link remainingSeconds} it makes ELAPSED
   * derivable (`plannedSeconds - remainingSeconds`) from the one take clock, so
   * nothing that needs elapsed time has to run a clock of its own.
   */
  plannedSeconds: number | null;
  /**
   * Whether the action bar's 3-state transport button may start a take
   * (wiki/feature-mobile-layout.md §"Start enablement"). False on focus entry
   * while content is still generating, true once the runner reaches its `ready`
   * stage, false again from the moment the take begins — so Start is never
   * live-but-dead (`startRecording()` no-ops outside `ready`). It is the
   * enablement of that control, beside `recordingState` and `remainingSeconds`.
   */
  startEnabled: boolean;
  /**
   * Monotonic generation epoch (RACE 1 — exit-during-generation). Bumped on
   * every {@link createFocusStore.enterFocus} AND on every {@link createFocusStore.exitFocus}
   * / {@link createFocusStore.reset}. The orchestrator captures this value
   * BEFORE it kicks off an async content generation (online SSE / offline LLM)
   * and, in the completion handler, NO-OPS if the live token has moved on —
   * i.e. the user Exited or restarted while generation was in flight. A
   * completed generation can therefore NEVER re-enter / re-paint focus after
   * Exit. Starts at 0; the value itself is opaque (only equality matters).
   */
  generationToken: number;
}

const INITIAL: Omit<FocusState, 'generationToken'> = {
  active: false,
  sessionName: null,
  practiceLabel: null,
  exitSpend: 'none',
  timer: null,
  recordingState: 'idle',
  pauseReason: null,
  remainingSeconds: null,
  plannedSeconds: null,
  startEnabled: false,
};

function createFocusStore() {
  // Monotonic epoch — survives `set()` (which replaces the whole record) by
  // being threaded through every transition. Bumped on start AND on end/reset
  // so a generation kicked off before an Exit/restart is detectably stale.
  let generationToken = 0;
  const { subscribe, set, update } = writable<FocusState>({
    ...INITIAL,
    generationToken,
  });

  return {
    subscribe,

    /**
     * The current generation epoch. The orchestrator reads this synchronously
     * BEFORE awaiting an async generation, then compares against it in the
     * completion handler (or calls {@link isGenerationStale}). Equality means
     * "still the same take"; inequality means the user Exited / restarted and
     * the completion must be dropped.
     */
    generationToken(): number {
      return generationToken;
    },

    /**
     * True when `token` is no longer the live epoch — i.e. an Exit / restart /
     * end happened since `token` was captured. RACE 1's by-construction guard:
     * an async generation completion calls this and bails when true, so a
     * finished generation can never re-enter focus after the user left.
     */
    isGenerationStale(token: number): boolean {
      return token !== generationToken;
    },

    /**
     * Enter focus mode. Called by a practice page when its Start CTA enters
     * focus + kicks off content generation — NOT when the take begins. The
     * recording-state starts at `'idle'`: the timer is frozen at `0:00 / m:ss`
     * and the only action-bar control is Exit. The single recording control
     * (§1) flips `recordingState` to `'recording'` only at the REAL recording
     * start — when frames actually flow, not at the click — via the
     * orchestrator's panel-anchored `startRecording` (which calls
     * {@link setRecordingState} once `RecordingPanel` reports the start anchor;
     * see wiki/feature-recording.md §"Recording-start anchor"). `timer` may be
     * null at start (the first frozen render fills it in).
     */
    enterFocus(opts: {
      sessionName?: string | null;
      practiceLabel?: string | null;
      timer?: string | null;
      remainingSeconds?: number | null;
    }): void {
      // RACE 1 — bump the epoch on every fresh entry so any generation kicked
      // off under a PRIOR token (a previous take the user Exited/restarted) is
      // now stale and its completion handler no-ops.
      generationToken += 1;
      set({
        active: true,
        sessionName: opts.sessionName ?? null,
        practiceLabel: opts.practiceLabel ?? null,
        // A fresh entry has charged nothing yet — the runner flips this when
        // the generation charge actually lands.
        exitSpend: 'none',
        timer: opts.timer ?? null,
        recordingState: 'idle',
        pauseReason: null,
        remainingSeconds: opts.remainingSeconds ?? null,
        plannedSeconds: null,
        startEnabled: false,
        generationToken,
      });
    },

    /**
     * Patch a subset of the focus state — used by the per-second timer tick
     * and by late-arriving session names.
     */
    update(partial: Partial<Omit<FocusState, 'active'>>): void {
      update((s) => ({ ...s, ...partial }));
    },

    /**
     * Set the recording-state machine without touching anything else. Called by
     * a practice page when the single recording control (and its action-bar
     * mirror) starts / pauses / resumes / ends the take. The shell observes the
     * new value and re-renders the action-bar controls per §5:
     *   - `idle`/`ended`  → Exit only, no mirror, no End.
     *   - `recording`     → "Pause recording" mirror + End.
     *   - `paused`        → "Resume recording" mirror + End.
     * The pause reason is kept only while paused, so leaving `paused` clears it.
     */
    setRecordingState(state: RecordingState, pauseReason: Exclude<PauseReason, null> = 'manual'): void {
      update((s) => ({ ...s, recordingState: state, pauseReason: state === 'paused' ? pauseReason : null }));
    },

    /**
     * Publish whether the action bar's transport button may start a take
     * (wiki/feature-mobile-layout.md §"Start enablement"). The runner mirrors
     * its lifecycle phase into this — true only at `ready`.
     */
    setStartEnabled(enabled: boolean): void {
      update((s) => ({ ...s, startEnabled: enabled }));
    },

    /**
     * Leave focus mode entirely. Called only by the Exit affordance (§4/§5)
     * and on teardown (unmount / layout reset). Returns to INITIAL —
     * `recordingState` back to `'idle'`, `active` false. NOTE: ending a take
     * does NOT call this — it flips `recordingState` to `'ended'` and keeps
     * focus mode active so the post-take content stays in the shell.
     */
    exitFocus(): void {
      // RACE 1 — bump on teardown too, so a generation in flight when the user
      // Exits is immediately stale (its completion can't re-paint a dead take).
      generationToken += 1;
      set({ ...INITIAL, generationToken });
    },

    /**
     * Hard reset — same as `end()` but spells out the intent at call sites
     * that are wiring teardown rather than a user-facing Exit.
     */
    reset(): void {
      generationToken += 1;
      set({ ...INITIAL, generationToken });
    },
  };
}

export const focusStore = createFocusStore();
